import { formatNumber } from 'utils/number';

/**
 * What to draw on the section profile, worked out as plain data before SciChart is
 * involved. Pure throughout, so the rules that matter are testable without a WASM
 * surface.
 */

/** Feet per mile, for the distance axis. */
const FT_PER_MI = 5280;

/**
 * How far into the section the interval names sit, as a fraction of its length.
 * Enough to clear the axis without floating away from the end it belongs to.
 */
const LABEL_INSET_FRACTION = 0.02;

/**
 * The margin at each end that counts as "crowded" when choosing which end to stack
 * the interval names at.
 */
const LABEL_EDGE_FRACTION = 0.22;

/** A section distance is in feet; a depth may be missing, which is a gap. */
export interface ProfilePoint {
  distanceFt: number;
  depthFt: number | null;
  /**
   * The sample in each reference, for the hover tooltip — the plotted depth alone
   * cannot answer in both. Optional because synthetic points (guides, sticks) have
   * no second reference to give.
   */
  tvdssFt?: number | null;
  tvdFt?: number | null;
}

/** One horizon (or any depth series) to draw. */
export interface ProfileSeries {
  id: string;
  /** The interval name, written at one end like a log header. */
  label: string;
  color: string;
  points: ProfilePoint[];
}

/**
 * A section vertex, as the map lettered it. A null label draws the guide line without
 * text — which is what Well to Well needs, where the vertex is a well and the letter
 * would be standing in for a wellbore.
 */
export interface ProfileVertex {
  distanceFt: number;
  label: string | null;
}

/** An unbroken run of samples, in the axis-value arrays SciChart wants. */
export interface PlanRun {
  xValues: number[];
  yValues: number[];
}

export interface PlanLine {
  id: string;
  color: string;
  runs: PlanRun[];
}

/**
 * A well's crossing of the plane. One per crossing, not one per well: a well that
 * crosses twice is at two distances, and both are real.
 */
export interface ProfileCrossing {
  /** The well, for focus. Every crossing of one well shares it. */
  uwi: string;
  /** The well's class, which picks the symbol it is drawn with. */
  status?: string | null;
  distanceFt: number;
  depthFt: number | null;
  /** The crossing in each reference, for the hover tooltip. */
  tvdssFt?: number | null;
  tvdFt?: number | null;
  color: string;
  /** The label a crossing carries, when labels are on. */
  label: string | null;
  /**
   * What positioned the well: `Survey`, `Interpreted` or `Estimated`, and null where
   * nothing did — the one case whose depth is a stand-in rather than a measurement.
   */
  geometrySourceKind: string | null;
  /** The section's own Well Workspace's edit of the bore, drawn beside the filed one. */
  workspaceEdited?: boolean;
}

/**
 * The series a crossing belongs to: one scatter per well, so focusing a well emphasises
 * every crossing of it at once.
 *
 * The section's own Well Workspace's edits are the exception, and are keyed by their
 * provenance as well. The workspace can be showing two of them for one bore — the staged
 * geometry it has already saved, and the one being dragged on the map right now — and
 * those are different answers to the same question, drawn with different symbols. A
 * series carries one marker for all of its points, so sharing a key would silently make
 * one of the two take the other's symbol.
 *
 * @param {ProfileCrossing} crossing - The crossing.
 * @return {string} Its series key.
 */
export const crossingSeriesKey = (crossing: ProfileCrossing): string => {
  if (!crossing.workspaceEdited) {
    return crossing.uwi;
  }
  return `${crossing.uwi}|${crossing.geometrySourceKind ?? 'edited'}`;
};

/**
 * A county the section runs through, as the plot names it: the county's name over the
 * stretch of section that lies inside it.
 */
export interface ProfileCounty {
  id: string;
  /** "MARTIN, TX", or just the county where the state is unknown. */
  label: string;
  /** Where the name sits: the middle of the county's stretch. */
  distanceFt: number;
}

/**
 * A defining well's wellbore, drawn at the vertex it defines. Excluded from the
 * crossings — a well does not cross a plane it defines. The stick runs KB to total
 * depth, so its length is the well's TVD, not a gap between two sampled surfaces.
 */
export interface ProfileStickInput {
  uwi: string;
  distanceFt: number;
  /** The shallower end: the well's surface elevation, in the plot's depth terms. */
  topFt: number;
  /** The deeper end: total depth, in the plot's depth terms. */
  bottomFt: number;
  color: string;
  /**
   * Its place along the plane, counting from one. The same number the map's dot and
   * the settings row carry, which is what lets one well be followed across all three
   * — so it is drawn whether or not the plot is annotated.
   */
  position: number;
  /** The well's name, or null where the record has none. */
  name: string | null;
  /** The well's status, for the card's Class line. */
  status: string | null;
  /** The hole's own length — KB down to total depth — for the card. */
  totalDepthFt: number | null;
}

/**
 * A bore as the plan hands it over: the caller's stick, plus where its name may grow.
 * The anchor is decided here because it depends on the distance domain, which is the
 * plan's to resolve.
 */
export interface ProfileStick extends ProfileStickInput {
  /**
   * Which edge of the name is pinned to the bore. In a two-well traverse the wells
   * ARE the section's ends, so a centred name is half outside the plot — it grows
   * inward from an end instead, the same way the interval names do.
   */
  labelAnchor: 'left' | 'center' | 'right';
  /**
   * What is written above the bore under its position: the well's name, then its uwi.
   * Empty once names are switched off — the position and the well's own colour stay,
   * because they are how the bore is identified rather than described.
   */
  nameLines: string[];
  /**
   * Whether even the position number is written. It is the last thing to go: a
   * traverse dense enough that the numbers themselves collide is one where the sticks
   * and their colours are the only honest annotation left.
   */
  showPosition: boolean;
}

/**
 * Where the plane passes through a fault. A span rather than a line: a mapped fault
 * is a ribbon with real width, so the section passes through it.
 */
export interface ProfileFault {
  id: string;
  name: string;
  startFt: number;
  endFt: number;
  color: string;
}

/**
 * A fault to band, with the depth its band starts at.
 *
 * A fault is a subsurface feature, so its band belongs under the ground rather than
 * standing up into the sky above it — which is what a full-height band did.
 */
export interface PlanFault extends ProfileFault {
  /**
   * Where the band's top sits: the terrain over the fault, or sea level where the
   * terrain is not drawn. Null on a flattened section, where no depth on the axis is
   * the ground and the band stays full height.
   */
  topDepthFt: number | null;
}

/**
 * A county crossing to rule, with the depth its rule rises from. Drawn in the air above
 * the ground rather than the rock below: the crossing is a fact about the surface, and
 * a line hanging into the subsurface reads as geology.
 */
export interface PlanCountyRule {
  distanceFt: number;
  /**
   * Where the rule starts, rising from there to the top of the plot: the terrain at that
   * distance, or sea level where the terrain is not drawn. Null on a flattened section,
   * where the axis has no surface on it and the rule spans the full height.
   */
  fromDepthFt: number | null;
}

/** An unbroken run of a filled interval: one x, two depths. */
export interface PlanBandRun {
  xValues: number[];
  topValues: number[];
  baseValues: number[];
}

export interface PlanBand {
  /** The pair, named for the interval whose top bounds it. */
  id: string;
  color: string;
  runs: PlanBandRun[];
}

/**
 * One county crossing as the caller hands it over — the same shape the response
 * carries, so nothing has to be reshaped on the way in.
 */
export interface ProfileBoundary {
  id: string;
  name: string;
  state: string | null;
  xFt: number;
  /**
   * True where the line steps INTO this county just past the crossing, false where it
   * steps out. Optional because a cut from a backend that predates the flag carries no
   * side at all, and the plot falls back to naming the pair on the boundary.
   */
  entering?: boolean;
}

/**
 * One end of the section, lettered. A and A′ say which end of the map's line the profile
 * is read from. They belong at the ends themselves: pinned to the plot's corners they
 * stayed put while the section panned under them, lettering whatever was there.
 */
export interface PlanEnd {
  label: string;
  distanceFt: number;
  /** Anchor the letter's right edge, so it grows inward from its own end. */
  anchorRight: boolean;
}

export interface PlanLabel {
  id: string;
  text: string;
  color: string;
  distanceFt: number;
  depthFt: number;
  /** Anchor the text on its right edge, so it grows inward from the section end. */
  anchorRight: boolean;
  /**
   * The stretch this grid has values over, so a name that follows the view stays on
   * its own line: panning past the grid's edge has to take the name away with it
   * rather than leave it floating over ground the grid never covered.
   */
  fromFt: number;
  toFt: number;
  /** The grid's own samples, for reading its depth wherever the name ends up. */
  points: ProfilePoint[];
}

export interface ProfilePlan {
  lines: PlanLine[];
  /** Crossings to mark, already filtered by whether they are shown. */
  crossings: ProfileCrossing[];
  /** Fault spans to band, already filtered by whether they are shown. */
  faults: PlanFault[];
  /** The defining wells' bores, in Well to Well. Empty in Arbitrary Line. */
  sticks: ProfileStick[];
  /**
   * Where a county line is crossed. One per distinct distance: at a shared line the
   * section leaves one county and enters the next at the same place, and that is one
   * line on the ground.
   */
  countyRules: PlanCountyRule[];
  /** The county names, each over its own stretch. */
  counties: ProfileCounty[];
  /** One band per adjacent pair of horizons, when fills are on. */
  bands: PlanBand[];
  ground: PlanRun[];
  labels: PlanLabel[];
  /** Intermediate vertices only: A and A′ are lettered by `ends` instead. */
  guides: ProfileVertex[];
  /** The section's two ends, lettered at the distances they actually sit at. */
  ends: PlanEnd[];
  /** The distance domain to pin the axis to, or null to let it auto-range. */
  xRange: [number, number] | null;
}

export interface ProfilePlanInput {
  series: ProfileSeries[];
  ground: ProfilePoint[] | null;
  showGround: boolean;
  /**
   * Hung on a datum, where no depth on the axis is the ground: surface features are
   * left unclipped rather than clipped against a surface the axis does not have.
   */
  flattened?: boolean;
  /** True where the axis counts downward, as TVD does — which way is shallower. */
  invertDepthAxis?: boolean;
  vertices: ProfileVertex[];
  /**
   * The drawn line's full extent. Pinning to it is what keeps the corner A / A′
   * labels on the section's real endpoints even where no grid reaches them; null
   * lets the axis close in on wherever there is data.
   */
  xRange: [number, number] | null;
  flipped: boolean;
  showLabels: boolean;
  /** Shade each interval between adjacent horizons. Off by default. */
  showFills?: boolean;
  /** The calculated crossings, when they have been asked for and are shown. */
  crossings?: ProfileCrossing[];
  /** The calculated fault spans, when they have been asked for and are shown. */
  faults?: ProfileFault[];
  /** The defining wells' bores, in Well to Well. */
  sticks?: ProfileStickInput[];
  /** The county crossings the cut returned, when they are shown. */
  boundaries?: ProfileBoundary[];
  /**
   * Guide the end vertices too. Off in Arbitrary Line, where A and A′ are already
   * drawn in the corners; on in Well to Well, where every vertex is a well and its
   * position along the section is the point.
   */
  guideEnds?: boolean;
  /**
   * Distances of anything else already drawn in the margins — wellbores, crossing
   * markers — so the interval names stack at the emptier end. Empty until wells
   * arrive, which is when the choice starts to matter.
   */
  crowdDistancesFt?: number[];
}

/**
 * Splits a series into unbroken runs, cutting wherever a depth is missing.
 *
 * A missing depth is not zero: a horizon that stops at the edge of its grid has to
 * break there rather than dive to sea level. Runs of one point are dropped, since a
 * single sample cannot draw a line — SciChart would take it and render nothing.
 *
 * @param {Array} points - The samples, in along-line order.
 * @return {Array} One run per unbroken stretch of at least two samples.
 */
/** How close, in CSS pixels, the pointer must be to a line before it reads as "on it". */
const HOVER_SNAP_PX = 12;

/**
 * How much further than the closest hit another may be and still share the tooltip.
 * Two horizons pinching together are one answer, not a fight over the pointer.
 */
const HOVER_TIE_PX = 8;

/** One key–value line of a tooltip group, e.g. `TVDSS  -6,614 ft`. */
export interface HoverPair {
  key: string;
  valueFt: number | null;
}

/** One thing under the pointer: a horizon, the ground, or a well crossing. */
export interface HoverGroup {
  label: string;
  color: string;
  pairs: HoverPair[];
}

/**
 * What the pointer is on, for the hover tooltip.
 *
 * A horizon is "under" the pointer when the pointer is within `HOVER_SNAP_PX` of the
 * line vertically at the hovered distance. Everything within `HOVER_TIE_PX` of the
 * closest hit shares the tooltip — a pinched-together stack reads as one answer. An
 * empty result means the pointer is on open plot, and no tooltip shows. Wells are
 * deliberately not here: the mock answers for a well on click, with the card, and a
 * hover that repeated the card would put the same answer in two places.
 *
 * Pixel geometry comes in as callbacks so this stays pure: the chart owns its
 * coordinate calculators, and a test can hand in the identity.
 *
 * @param {object} content - The series, the ground profile, and the crossings drawn.
 * @param {number} distanceFt - The hovered distance along the section.
 * @param {number} cursorYPx - The pointer's vertical position, in CSS pixels.
 * @param {Function} depthToPx - Plotted depth to CSS pixels.
 * @return {HoverGroup[]} The groups to show, nearest first.
 */
export const hoverGroupsAt = (
  content: {
    series: ProfileSeries[];
    ground: ProfilePoint[] | null;
  },
  distanceFt: number,
  cursorYPx: number,
  depthToPx: (depthFt: number) => number
): HoverGroup[] => {
  const hits: Array<HoverGroup & { gap: number }> = [];

  /**
   * The series point nearest the hovered distance that is actually drawn there.
   *
   * @param {ProfilePoint[]} points - The series' samples, in distance order.
   * @return {ProfilePoint | null} The nearest drawable point.
   */
  const nearestAt = (points: ProfilePoint[]): ProfilePoint | null => {
    let best: ProfilePoint | null = null;
    let bestGap = Infinity;
    points.forEach((point) => {
      if (point.depthFt === null) {
        return;
      }
      const gap = Math.abs(point.distanceFt - distanceFt);
      if (gap < bestGap) {
        bestGap = gap;
        best = point;
      }
    });
    return best;
  };

  content.series.forEach((line) => {
    const point = nearestAt(line.points);
    if (point === null || point.depthFt === null) {
      return;
    }
    const gap = Math.abs(depthToPx(point.depthFt) - cursorYPx);
    if (gap > HOVER_SNAP_PX) {
      return;
    }
    hits.push({
      label: line.label,
      color: line.color,
      pairs: [
        { key: 'TVD', valueFt: point.tvdFt ?? null },
        { key: 'TVDSS', valueFt: point.tvdssFt ?? null },
      ],
      gap,
    });
  });

  const groundPoint = content.ground ? nearestAt(content.ground) : null;
  if (groundPoint !== null && groundPoint.depthFt !== null) {
    const gap = Math.abs(depthToPx(groundPoint.depthFt) - cursorYPx);
    if (gap <= HOVER_SNAP_PX) {
      hits.push({
        label: 'Surface Terrain',
        color: GROUND_HOVER_COLOR,
        // Ground level is one number; TVD of the ground is zero by definition and
        // saying so would be noise.
        pairs: [{ key: 'GL', valueFt: groundPoint.tvdssFt ?? null }],
        gap,
      });
    }
  }

  if (hits.length === 0) {
    return [];
  }
  hits.sort((a, b) => a.gap - b.gap);
  const closest = hits[0].gap;
  return hits
    .filter((hit) => hit.gap <= closest + HOVER_TIE_PX)
    .map((hit) => ({ label: hit.label, color: hit.color, pairs: hit.pairs }));
};

/** The ground line's own grey, for its tooltip swatch. */
const GROUND_HOVER_COLOR = '#8d8d8d';

/**
 * How close, in CSS pixels, a click must land to pick a well — wider than the symbol on
 * purpose. Ten asked for surgical aim: UAT called the interaction "completely missing",
 * and instrumenting it showed real clicks landing ten to twelve pixels off the mark.
 */
const WELL_PICK_PX = 16;

/**
 * The well a click lands on, if any.
 *
 * A defining well's bore is hit within `WELL_PICK_PX` of its distance, anywhere along
 * its drawn depth (give or take the same reach); a crossing is hit within that reach
 * of its dot in both axes together. The nearest wins, and open plot answers null —
 * which is also how a selection is put away.
 *
 * @param {object} content - The sticks and crossings drawn.
 * @param {number} cursorXPx - The click, horizontally, in CSS pixels.
 * @param {number} cursorYPx - The click, vertically, in CSS pixels.
 * @param {Function} distanceToPx - Distance to CSS pixels.
 * @param {Function} depthToPx - Plotted depth to CSS pixels.
 * @return {string | null} The picked well's uwi, or null for open plot.
 */
export const wellAt = (
  content: { sticks: ProfileStickInput[]; crossings: ProfileCrossing[] },
  cursorXPx: number,
  cursorYPx: number,
  distanceToPx: (distanceFt: number) => number,
  depthToPx: (depthFt: number) => number
): string | null => {
  let best: string | null = null;
  let bestGap = Infinity;

  content.sticks.forEach((stick) => {
    const dx = Math.abs(distanceToPx(stick.distanceFt) - cursorXPx);
    if (dx > WELL_PICK_PX || dx >= bestGap) {
      return;
    }
    const top = depthToPx(stick.topFt);
    const bottom = depthToPx(stick.bottomFt);
    const lower = Math.min(top, bottom) - WELL_PICK_PX;
    const upper = Math.max(top, bottom) + WELL_PICK_PX;
    if (cursorYPx < lower || cursorYPx > upper) {
      return;
    }
    best = stick.uwi;
    bestGap = dx;
  });

  content.crossings.forEach((crossing) => {
    if (crossing.depthFt === null) {
      return;
    }
    const gap = Math.hypot(
      distanceToPx(crossing.distanceFt) - cursorXPx,
      depthToPx(crossing.depthFt) - cursorYPx
    );
    if (gap > WELL_PICK_PX || gap >= bestGap) {
      return;
    }
    best = crossing.uwi;
    bestGap = gap;
  });

  return best;
};

export const splitRuns = (points: ProfilePoint[]): PlanRun[] => {
  const runs: PlanRun[] = [];
  let xValues: number[] = [];
  let yValues: number[] = [];

  const flush = () => {
    if (xValues.length >= 2) {
      runs.push({ xValues, yValues });
    }
    xValues = [];
    yValues = [];
  };

  points.forEach((point) => {
    if (point.depthFt === null) {
      flush();
      return;
    }
    xValues.push(point.distanceFt);
    yValues.push(point.depthFt);
  });
  flush();

  return runs;
};

/**
 * The filled intervals: one band per adjacent pair of horizons, coloured by the upper
 * one — the interval belongs to the formation whose top bounds it.
 *
 * Samples are paired by index, since every grid on a section is sampled at the same
 * distances. A gap in either horizon breaks the band there rather than bridging
 * across ground the pair does not both cover.
 *
 * @param {Array} series - The horizon stack, in stratigraphic order.
 * @return {Array} One band per pair that has something to fill.
 */
const buildBands = (series: ProfileSeries[]): PlanBand[] => {
  const bands: PlanBand[] = [];

  for (let index = 0; index < series.length - 1; index += 1) {
    const upper = series[index];
    const lower = series[index + 1];
    const runs: PlanBandRun[] = [];
    let xValues: number[] = [];
    let topValues: number[] = [];
    let baseValues: number[] = [];

    const flush = () => {
      if (xValues.length >= 2) {
        runs.push({ xValues, topValues, baseValues });
      }
      xValues = [];
      topValues = [];
      baseValues = [];
    };

    const count = Math.min(upper.points.length, lower.points.length);
    for (let i = 0; i < count; i += 1) {
      const top = upper.points[i];
      const base = lower.points[i];
      if (top.depthFt === null || base.depthFt === null) {
        flush();
        continue;
      }
      xValues.push(top.distanceFt);
      topValues.push(top.depthFt);
      baseValues.push(base.depthFt);
    }
    flush();

    if (runs.length > 0) {
      bands.push({ id: upper.id, color: upper.color, runs });
    }
  }

  return bands;
};

/**
 * A section distance in the chosen unit. Basin-scale lines run to several hundred
 * thousand feet, where every tick is a six-digit number, so the axis reads in miles
 * as well — to a tenth under ten miles, where the tenth still carries information.
 *
 * When the visible span is passed, the precision follows the zoom instead: a window a
 * few hundred feet wide has every tick inside the same tenth of a mile, and an axis
 * whose labels all read "0.8" is an axis saying nothing.
 *
 * @param {number} feet - The distance along the section.
 * @param {string} unit - Either `ft` or `mi`.
 * @param {number} [visibleSpanFt] - The distance the axis currently shows.
 * @return {string} The formatted distance, with thousands separators.
 */
export const formatSectionDistance = (
  feet: number,
  unit: 'ft' | 'mi',
  visibleSpanFt?: number
): string => {
  if (unit !== 'mi') {
    return formatAxisTick(feet, 0);
  }
  const miles = feet / FT_PER_MI;
  if (visibleSpanFt !== undefined && visibleSpanFt > 0) {
    // Enough decimals that ticks a tenth of the window apart read differently.
    const spanMiles = visibleSpanFt / FT_PER_MI;
    const precision = Math.min(
      3,
      Math.max(0, Math.ceil(-Math.log10(spanMiles / 10)))
    );
    return formatAxisTick(miles, precision);
  }
  // Under ten miles the tenth carries information; past that it is noise on a
  // number already only as good as the sampled section geometry.
  return Math.abs(miles) < 10
    ? formatAxisTick(miles, 1)
    : formatAxisTick(miles, 0);
};

/**
 * An axis tick: fixed decimals, and negative zero straightened out.
 *
 * Fixed rather than trimmed decimals, because an axis reads as a column — "0.8" next
 * to "0.805" hides that the two are the same unit at the same precision. And the axes
 * grow a small margin past their data, so a tick can land a hair past zero and round
 * to "-0" — a sign carrying no information on a value that is zero at this precision.
 *
 * @param {number} value - The tick value.
 * @param {number} precision - Decimal places to show, exactly.
 * @return {string} The label.
 */
export const formatAxisTick = (value: number, precision: number): string => {
  const label = formatNumber(value, precision, precision);
  return /^-0(\.0*)?$/.test(label) ? label.slice(1) : label;
};

/**
 * What the depth axis is measuring, in the words the header shows.
 *
 * @param {object} options - The depth reference and the datum interval, if any.
 * @return {string} The axis title.
 */
export const depthAxisTitle = ({
  depthReference,
  datumInterval,
}: {
  depthReference: 'tvdss' | 'tvd';
  datumInterval?: string | null;
}): string => {
  if (datumInterval) {
    return `Depth relative to ${datumInterval} (ft)`;
  }
  return depthReference === 'tvd' ? 'TVD (ft)' : 'TVDSS (ft)';
};

/**
 * Whether the depth axis runs the other way. TVD counts downward from ground level,
 * so larger numbers are deeper; TVDSS is already negative downward, and flattening
 * to a datum overrides the reference entirely.
 *
 * @param {object} options - The depth reference and the datum interval, if any.
 * @return {boolean} True when the axis has to be inverted.
 */
export const invertDepthAxis = ({
  depthReference,
  datumInterval,
}: {
  depthReference: 'tvdss' | 'tvd';
  datumInterval?: string | null;
}): boolean => !datumInterval && depthReference === 'tvd';

/**
 * Which way a bore's name should grow.
 *
 * A well within a whisker of either end of the section would have half its name
 * clipped by the plot edge, so the name is anchored on the inward side there. The
 * threshold is a fraction of the section's length rather than a fixed distance: what
 * counts as "at the end" depends on how long the section is.
 *
 * @param {number} distanceFt - Where the well stands along the section.
 * @param {Array | null} xRange - The distance domain, when it is pinned.
 * @return {string} The anchor to use.
 */
const EDGE_FRACTION = 0.02;

/**
 * Plans the sticks' labels, degrading each one to what its room can hold.
 *
 * A bore's label competes with its neighbours for the space between them: on a dense
 * traverse the full name-and-uwi form overprints into an unreadable smear. Each stick
 * therefore steps down a ladder — name and uwi, name alone, the position number, then
 * nothing — taking the first form that fits the gap to its nearest neighbour. The
 * number goes last because it is how a bore is identified across the plot, the map
 * and the settings list; and the stick itself, with its status colour, never goes.
 *
 * Room is measured in the distance domain the way the county names measure theirs,
 * so the ladder is decided by the section, not by the pixel width of one screen.
 *
 * @param {ProfileStickInput[]} sticks - The defining wells' bores.
 * @param {Array | null} xRange - The distance domain, when it is pinned.
 * @param {boolean} showLabels - Whether the plot is annotated at all.
 * @return {ProfileStick[]} The sticks, each with the label it has room for.
 */
const planSticks = (
  sticks: ProfileStickInput[],
  xRange: [number, number] | null,
  showLabels: boolean
): ProfileStick[] => {
  // Every bore is named, whatever the traverse's density. It used to degrade rung by
  // rung — name and uwi, name, the number, nothing — against the room between
  // neighbours, which left a crowded traverse showing bare position numbers and no way
  // to learn a well's name: the rung was picked from the whole line's span, so zooming
  // in never brought it back. A crowded label is worse to read than a sparse one, and
  // better than one that is not there.
  return sticks.map((stick) => ({
    ...stick,
    nameLines: showLabels
      ? [stick.name, stick.uwi].filter((line): line is string => Boolean(line))
      : [],
    showPosition: true,
    labelAnchor: stickLabelAnchor(stick.distanceFt, xRange),
  }));
};

const stickLabelAnchor = (
  distanceFt: number,
  xRange: [number, number] | null
): 'left' | 'center' | 'right' => {
  if (xRange === null) {
    return 'center';
  }
  const [start, end] = xRange;
  const margin = (end - start) * EDGE_FRACTION;
  if (distanceFt <= start + margin) {
    return 'left';
  }
  if (distanceFt >= end - margin) {
    return 'right';
  }
  return 'center';
};

/**
 * A distance as a grouping key, to a tenth of a foot. Two intersections of the same
 * boundary can differ in the last decimal, and they are the same place.
 *
 * @param {number} distanceFt - The distance.
 * @return {string} The key.
 */
/**
 * Roughly what one character of a county name takes up, as a share of the section on
 * show. Deliberately generous: overestimating costs a little placement accuracy, while
 * underestimating costs a whole name.
 */
const COUNTY_LABEL_CHAR_SPAN = 0.006;

const tenthKey = (distanceFt: number): string =>
  String(Math.round(distanceFt * 10));

/**
 * A sampled line's plotted depth at a distance along the section.
 *
 * Interpolated between the samples either side, because a line is sampled on the cut's
 * own step and a fault, a county line or an interval name falls wherever it falls. A
 * distance in a gap — or off the sampled stretch entirely — has no answer, which is
 * different from an answer of zero.
 *
 * @param {Array | null} points - The samples, in along-line order.
 * @param {number} distanceFt - Where along the section to read it.
 * @return {number | null} The plotted depth, or null where the line does not reach.
 */
export const depthAtDistance = (
  points: ProfilePoint[] | null,
  distanceFt: number
): number | null => {
  if (!points || points.length === 0) {
    return null;
  }
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from.distanceFt > distanceFt || to.distanceFt < distanceFt) {
      continue;
    }
    if (from.depthFt === null || to.depthFt === null) {
      return null;
    }
    const span = to.distanceFt - from.distanceFt;
    if (span <= 0) {
      return from.depthFt;
    }
    const along = (distanceFt - from.distanceFt) / span;
    return from.depthFt + along * (to.depthFt - from.depthFt);
  }
  return null;
};

/**
 * Where the surface sits at a distance, for anything drawn against it.
 *
 * The terrain when it is drawn, and zero — sea level in TVDSS, ground level in TVD —
 * when it is not: a surface feature still stops somewhere, and zero is the reference
 * the axis already has. Null on a flattened section, where depths are read against a
 * datum and no value on the axis is the surface.
 *
 * @param {ProfilePlanInput} input - The plan input, for the ground and the mode.
 * @param {number} distanceFt - Where along the section to read it.
 * @return {number | null} The depth to stop at, or null to leave it unclipped.
 */
const surfaceDepthAt = (
  input: ProfilePlanInput,
  distanceFt: number
): number | null => {
  if (input.flattened) {
    return null;
  }
  if (input.showGround) {
    const ground = depthAtDistance(input.ground, distanceFt);
    if (ground !== null) {
      return ground;
    }
  }
  return 0;
};

/**
 * The distances where a county line is crossed, each once.
 *
 * Crossing from one county into the next puts two markers at the same place — each
 * county's own boundary is met — but on the ground that is a single line, so it is
 * drawn once. Compared as fixed distances rather than exactly, because the two come
 * from separate intersections and can differ in the last decimal.
 *
 * @param {Array} boundaries - The county crossings, in along-line order.
 * @return {Array} The distinct distances, ascending.
 */
const countyRulesFrom = (boundaries: ProfileBoundary[]): number[] => {
  const seen = new Set<string>();
  const rules: number[] = [];
  [...boundaries]
    .sort((a, b) => a.xFt - b.xFt)
    .forEach((boundary) => {
      const key = tenthKey(boundary.xFt);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      rules.push(boundary.xFt);
    });
  return rules;
};

/**
 * Each county named over the stretch of section that lies inside it — the mock's
 * treatment — wherever the response determines which stretch that is.
 *
 * The stretch follows from the crossings. A county the section passes right through is
 * crossed twice, and the stretch is between them. A county crossed once is one the
 * section began or ended inside, and the stretch runs from its crossing to whichever
 * end of the section is on its side: the earliest crossing of all belongs to the
 * county the section started in, the latest to the county it finished in.
 *
 * That leaves one case the response cannot settle. Where two counties are each crossed
 * once at the SAME distance — a section that crosses exactly one county line — either
 * could be the one on the left. `crossingIndex` does not help: it is each county's own
 * ordinal, so both read 0. Those are named on the boundary instead of over a stretch,
 * which says what is known rather than guessing a side and naming the wrong half of the
 * plot.
 *
 * @param {Array} boundaries - The county crossings.
 * @param {Array | null} xRange - The distance domain, when it is pinned.
 * @return {Array} One name per county, placed over its stretch where that is known.
 */
const countiesFrom = (
  boundaries: ProfileBoundary[],
  xRange: [number, number] | null
): ProfileCounty[] => {
  if (boundaries.length === 0) {
    return [];
  }
  const rules = countyRulesFrom(boundaries);
  const [start, end] = xRange ?? [rules[0], rules[rules.length - 1]];

  const byCounty = new Map<string, ProfileBoundary[]>();
  boundaries.forEach((boundary) => {
    const key = boundary.id;
    byCounty.set(key, [...(byCounty.get(key) ?? []), boundary]);
  });

  // How many counties are crossed exactly once at each distance: two of them there is
  // the case that cannot be settled.
  const singlesAt = new Map<string, number>();
  byCounty.forEach((crossings) => {
    if (crossings.length !== 1) {
      return;
    }
    const key = tenthKey(crossings[0].xFt);
    singlesAt.set(key, (singlesAt.get(key) ?? 0) + 1);
  });

  const named: ProfileCounty[] = [];
  const unsettled: ProfileBoundary[] = [];

  byCounty.forEach((crossings, id) => {
    const sorted = [...crossings].sort((a, b) => a.xFt - b.xFt);
    const span = stretchOf(sorted, rules, singlesAt, start, end);
    if (span === null) {
      unsettled.push(sorted[0]);
      return;
    }
    // Only the part of the stretch on show: cropping can leave a county off the plot
    // entirely, and a name centred on an unseen stretch ends up pinned to the edge
    // naming the wrong ground. Without a domain the ends fall back to the crossings.
    const visible = xRange ? clipToDomain(span, start, end) : span;
    if (visible === null) {
      return;
    }
    named.push({
      id,
      label: countyLabel([sorted[0]]),
      distanceFt: (visible[0] + visible[1]) / 2,
    });
  });

  // The unsettled ones read as a pair on the line they share.
  const byDistance = new Map<string, ProfileBoundary[]>();
  unsettled.forEach((crossing) => {
    const key = tenthKey(crossing.xFt);
    byDistance.set(key, [...(byDistance.get(key) ?? []), crossing]);
  });
  byDistance.forEach((crossings, key) => {
    const at = crossings[0].xFt;
    if (xRange && (at < start || at > end)) {
      return;
    }
    named.push({
      id: `boundary-${key}`,
      label: countyLabel(crossings),
      distanceFt: at,
    });
  });

  return named.sort((a, b) => a.distanceFt - b.distanceFt);
};

/**
 * The part of a stretch that falls inside the domain on show.
 *
 * @param {Array} span - The stretch, either way round.
 * @param {number} start - The domain's start.
 * @param {number} end - The domain's end.
 * @return {Array | null} The visible part, or null where none of it is.
 */
const clipToDomain = (
  span: [number, number],
  start: number,
  end: number
): [number, number] | null => {
  const from = Math.max(Math.min(span[0], span[1]), start);
  const to = Math.min(Math.max(span[0], span[1]), end);
  // Collapsed to a point means the stretch only touched the edge of the domain, which
  // is a county on the far side of the crop rather than one a hair wide.
  return from >= to ? null : [from, to];
};

/**
 * Moves county names apart where two would print over each other.
 *
 * A name sits over the middle of its stretch, and on a line that doubles back two
 * stretches can have middles a few thousand feet apart — close enough that the two
 * names overlap and neither reads. Nudging them apart keeps both: a name slightly off
 * the centre of its stretch is still plainly that stretch's, where two names on top of
 * each other are nobody's.
 *
 * The room a name needs is estimated from its length as a share of the section, so it
 * holds at any zoom without measuring text.
 *
 * @param {Array} counties - The names, in along-line order.
 * @param {Array | null} xRange - The distance domain, when it is pinned.
 * @return {Array} The names, moved as little as possible.
 */
const spreadCountyLabels = (
  counties: ProfileCounty[],
  xRange: [number, number] | null
): ProfileCounty[] => {
  if (counties.length < 2 || xRange === null) {
    return counties;
  }
  const span = xRange[1] - xRange[0];
  const widest = Math.max(...counties.map((county) => county.label.length));
  const gap = widest * COUNTY_LABEL_CHAR_SPAN * span;
  const placed = spreadLabels(
    counties.map((county) => county.distanceFt),
    gap,
    xRange
  );
  return counties.map((county, index) => ({
    ...county,
    distanceFt: placed[index],
  }));
};

/**
 * The stretch of section one county covers, or null where the response cannot say.
 *
 * @param {Array} crossings - That county's crossings, in along-line order.
 * @param {Array} rules - Every distinct crossing distance on the section.
 * @param {Map} singlesAt - How many once-crossed counties sit at each distance.
 * @param {number} start - The section's start.
 * @param {number} end - The section's end.
 * @return {Array | null} The stretch, or null when the side is undecidable.
 */
const stretchOf = (
  crossings: ProfileBoundary[],
  rules: number[],
  singlesAt: Map<string, number>,
  start: number,
  end: number
): [number, number] | null => {
  if (crossings.length > 1) {
    // Entered and left, so the stretch is between the first two: a section that leaves
    // and re-enters is named over the first pass rather than across the gap.
    return [crossings[0].xFt, crossings[1].xFt];
  }

  // Which way the line steps across settles which side the county is on: entering means
  // ahead of the crossing, leaving means behind. This is what makes a section crossing
  // exactly one county line nameable, where both ordinals are zero.
  if (crossings[0].entering !== undefined) {
    return crossings[0].entering
      ? [crossings[0].xFt, end]
      : [start, crossings[0].xFt];
  }

  const only = crossings[0].xFt;
  const key = tenthKey(only);
  if ((singlesAt.get(key) ?? 0) > 1) {
    return null;
  }
  if (key === tenthKey(rules[0])) {
    return [start, only];
  }
  if (key === tenthKey(rules[rules.length - 1])) {
    return [only, end];
  }
  // A single crossing in the middle of the line: the county is on one side of it and
  // nothing here says which.
  return null;
};

/**
 * A county's name as the plot writes it: without the word "County", which every one
 * of them carries and none of them needs — the mock labels a crossing "MARTIN, TX".
 * Only a trailing occurrence goes, so a county actually named for one keeps it.
 *
 * @param {ProfileBoundary} crossing - The crossing.
 * @return {string} The name to show.
 */
const countyName = (crossing: ProfileBoundary): string =>
  crossing.name.replace(/\s+County$/i, '');

/**
 * How the counties at one crossing read. A single county keeps the mock's form,
 * "MARTIN, TX". Two counties meeting share the state where they are in the same one,
 * which they nearly always are.
 *
 * @param {Array} crossings - The crossings at one distance.
 * @return {string} The label.
 */
const countyLabel = (crossings: ProfileBoundary[]): string => {
  const names = [...new Set(crossings.map((crossing) => countyName(crossing)))];
  const states = [
    ...new Set(crossings.map((crossing) => crossing.state ?? '')),
  ];
  const joined = names.join(' / ');
  if (states.length === 1 && states[0] !== '') {
    return `${joined}, ${states[0]}`;
  }
  return crossings
    .map((crossing) => {
      const name = countyName(crossing);
      if (!crossing.state) {
        return name;
      }
      return `${name}, ${crossing.state}`;
    })
    .join(' / ');
};

/**
 * How much room the ends of a Well-to-Well traverse get, as a share of its length — a
 * share, because half a label is a constant number of pixels however long the span is.
 * Sized for real names: at four percent a two-well traverse was still shorn both ends.
 */
const TRAVERSE_END_PAD_FRACTION = 0.08;

/**
 * The distance domain for a Well-to-Well traverse: the wells' own span, with a little
 * room at each end.
 *
 * The first and last wells sit exactly on the line's ends, so an axis drawn to the span
 * itself puts them against the frame and cuts their labels in half. Padding the domain
 * moves the frame rather than the wells, which is the only way to centre a label over a
 * well that sits on the boundary.
 *
 * @param {number} startFt - Where the traverse starts.
 * @param {number} endFt - Where it ends.
 * @return {Array} The padded domain.
 */
export const paddedTraverseRange = (
  startFt: number,
  endFt: number
): [number, number] => {
  const span = endFt - startFt;
  if (!Number.isFinite(span) || span <= 0) {
    return [startFt, endFt];
  }
  const pad = span * TRAVERSE_END_PAD_FRACTION;
  return [startFt - pad, endFt + pad];
};

/**
 * The distance domain the axis should cover: the drawn line where it is known,
 * otherwise the stretch the grids have values over.
 *
 * The caller withholds the line's extent to crop the section to its grids, so the
 * grids are the only thing that decides the domain here. The ground is deliberately
 * left out of it: the DEM covers ground the grids never reach, and counting it made
 * cropping scope the section to the terrain's coverage instead of the grids' — which
 * is to say, made it look like cropping did nothing.
 *
 * @param {ProfilePlanInput} input - The plan input.
 * @return {Array | null} The domain, or null when no grid has values.
 */
const resolveXRange = ({
  series,
  xRange,
}: ProfilePlanInput): [number, number] | null => {
  if (xRange) {
    return xRange;
  }
  const distances = series.flatMap((line) =>
    line.points.filter((p) => p.depthFt !== null).map((p) => p.distanceFt)
  );
  if (distances.length < 2) {
    return null;
  }
  return [Math.min(...distances), Math.max(...distances)];
};

/**
 * Where the interval names go: stacked at one end of the section like a log header
 * rather than scattered along the lines, so the stack can be read as a list.
 *
 * Which end is whichever has fewer wells in its margin — a name sitting over a
 * wellbore is the one collision worth avoiding, since everything else at the edges
 * is on its own row above the data.
 *
 * @param {ProfilePlanInput} input - The plan input.
 * @param {Array | null} domain - The resolved distance domain.
 * @return {Array} One label per series that has a line to label.
 */
const buildLabels = (
  input: ProfilePlanInput,
  domain: [number, number] | null
): PlanLabel[] => {
  if (!input.showLabels || input.series.length === 0) {
    return [];
  }

  const span = domain ? domain[1] - domain[0] : 0;
  let atStart = true;
  if (domain && span > 0) {
    const band = span * LABEL_EDGE_FRACTION;
    const crowd = input.crowdDistancesFt ?? [];
    const startCrowd = crowd.filter(
      (d) => d >= domain[0] && d <= domain[0] + band
    ).length;
    const endCrowd = crowd.filter(
      (d) => d >= domain[1] - band && d <= domain[1]
    ).length;
    // Ties favour the start, so an empty section labels consistently.
    atStart = startCrowd <= endCrowd;
  }

  const target = domain
    ? atStart
      ? domain[0] + span * LABEL_INSET_FRACTION
      : domain[1] - span * LABEL_INSET_FRACTION
    : null;

  // The data-min end is on the left until the axis is flipped, and the text has to
  // grow inward from whichever side that turns out to be.
  const onLeft = atStart !== input.flipped;

  return input.series.flatMap((line) => {
    const valid = line.points.filter(
      (point): point is { distanceFt: number; depthFt: number } =>
        point.depthFt !== null
    );
    if (valid.length < 2) {
      return [];
    }
    // The nearest sample that actually has a value, so a name never floats over a
    // gap where this grid stops short of the section end.
    const at =
      target === null
        ? valid[0]
        : valid.reduce((best, point) => {
            const closer =
              Math.abs(point.distanceFt - target) <
              Math.abs(best.distanceFt - target);
            return closer ? point : best;
          });
    return [
      {
        id: line.id,
        text: line.label,
        color: line.color,
        distanceFt: at.distanceFt,
        depthFt: at.depthFt,
        anchorRight: !onLeft,
        fromFt: valid[0].distanceFt,
        toFt: valid[valid.length - 1].distanceFt,
        points: line.points,
      },
    ];
  });
};

/**
 * The section's two ends, lettered where they are.
 *
 * Taken from the vertices rather than from the axis, because the vertices are in
 * along-line order whichever way the section is read: the first is always A. Flipping
 * reverses the axis, not the line, so it decides only which way each letter grows —
 * always inward, so neither is half off the plot.
 *
 * @param {Array} vertices - The section's vertices, in along-line order.
 * @param {boolean} flipped - True where the section is read right to left.
 * @return {Array} The two ends, or none where there is no line yet.
 */
const endsFrom = (vertices: ProfileVertex[], flipped: boolean): PlanEnd[] => {
  if (vertices.length < 2) {
    return [];
  }
  return [
    { label: 'A', distanceFt: vertices[0].distanceFt, anchorRight: flipped },
    {
      label: "A'",
      distanceFt: vertices[vertices.length - 1].distanceFt,
      anchorRight: !flipped,
    },
  ];
};

/**
 * Everything the profile draws, as data.
 *
 * @param {ProfilePlanInput} input - The series, the ground, and the display state.
 * @return {ProfilePlan} The drawing plan.
 */
export const buildProfilePlan = (input: ProfilePlanInput): ProfilePlan => {
  const xRange = resolveXRange(input);

  const plan: ProfilePlan = {
    lines: input.series.map((line) => ({
      id: line.id,
      color: line.color,
      runs: splitRuns(line.points),
    })),
    bands: input.showFills ? buildBands(input.series) : [],
    // Both arrive already filtered: whether a calculated result is drawn is the
    // caller's switch, and re-deciding it here would put the rule in two places.
    crossings: input.crossings ?? [],
    // A fault band hangs from the terrain over the middle of the band: a mapped fault
    // is a few hundred feet wide at most, over which the ground moves by a few feet,
    // so one depth for the band reads as level with the ground at either end of it.
    faults: (input.faults ?? []).map((fault) => ({
      ...fault,
      topDepthFt: surfaceDepthAt(input, (fault.startFt + fault.endFt) / 2),
    })),
    countyRules: countyRulesFrom(input.boundaries ?? []).map((distanceFt) => ({
      distanceFt,
      fromDepthFt: surfaceDepthAt(input, distanceFt),
    })),
    counties: spreadCountyLabels(
      countiesFrom(input.boundaries ?? [], xRange),
      xRange
    ),
    // The name and uwi obey the same labels switch the interval names do — one rule
    // for whether the plot is annotated — and past that, each label degrades to what
    // the room between neighbouring bores can hold.
    sticks: planSticks(input.sticks ?? [], xRange, input.showLabels),
    ground: input.showGround && input.ground ? splitRuns(input.ground) : [],
    labels: buildLabels(input, xRange),
    // The endpoints are lettered by `ends`, so a guide through them would only repeat
    // what the letter says — unless the vertices are wells, where the guide marks a
    // well rather than restating a letter.
    guides: input.guideEnds
      ? input.vertices
      : input.vertices.filter(
          (vertex) => vertex.label !== 'A' && vertex.label !== "A'"
        ),
    ends: endsFrom(input.vertices, input.flipped),
    xRange,
  };

  // Placeholder depths are settled last: the surface answers for most of them, and
  // where it cannot the extent of everything measured does — which is only known once
  // the rest of the plan exists.
  return {
    ...plan,
    crossings: placePlaceholders(
      plan.crossings,
      input,
      depthExtentOf(plan, { measuredOnly: true })
    ),
  };
};

/**
 * The span of depths the plot has to hold: every line, the ground, the defining wells'
 * bores and the crossings.
 *
 * Kept here, as plain data, because it is what the depth axis has to be fitted to and
 * SciChart will not work it out again on its own — the axis fits its data the first
 * time it draws and then keeps whatever range it has, so switching the ground line on
 * afterwards would otherwise draw it above the top of the plot.
 *
 * @param {ProfilePlan} plan - The drawing plan.
 * @return {Array | null} The [shallowest, deepest] pair, or null with nothing to fit.
 */
export const depthExtentOf = (
  plan: ProfilePlan,
  { measuredOnly = false }: { measuredOnly?: boolean } = {}
): [number, number] | null => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  const consider = (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      return;
    }
    min = Math.min(min, value);
    max = Math.max(max, value);
  };

  plan.lines.forEach((line) => {
    line.runs.forEach((run) => run.yValues.forEach(consider));
  });
  plan.ground.forEach((run) => run.yValues.forEach(consider));
  plan.sticks.forEach((stick) => {
    consider(stick.topFt);
    consider(stick.bottomFt);
  });
  // Every crossing counts, including one standing at the surface because nothing
  // measured its depth: it is drawn there, so the axis has to reach it. `measuredOnly`
  // serves the one caller that asks before those wells have been placed, where the depth
  // still reads as the backend's stand-in thousand feet and fitting to it would squeeze
  // the horizons that were measured.
  plan.crossings.forEach((crossing) => {
    if (!measuredOnly || !hasNoMeasuredDepth(crossing)) {
      consider(crossing.depthFt);
    }
  });

  if (min === Number.POSITIVE_INFINITY) {
    return null;
  }
  return [min, max];
};

/**
 * How far above the shallowest thing drawn a placeholder crossing sits, as a share of
 * the depth on show. Inside the margin the axis already grows past its data, so the
 * marker lands on the plot rather than off the top of it.
 */
const PLACEHOLDER_LIFT = 0.06;

/**
 * The nominal TVD the backend stands a well at when it has neither a survey nor a
 * reported total depth (`Query::CrossSectionWellHits::FALLBACK_TVD_FT`).
 *
 * Only reaches a section where the DEM has no ground under the well, which is the one
 * case the backend cannot re-reference and leaves at sea level. Recognised by value,
 * for want of anything else there: a well with a reported TVD carries no kind either,
 * and that depth is real.
 */
const NOMINAL_FALLBACK_TVD_FT = 1_000;

/**
 * Whether something actually put the well where it is drawn.
 *
 * A survey, a pre-drill plan and an estimated vertical stick all do — each is real
 * geometry, and a well positioned by any of them is drawn as the well it is. Only a
 * well with no geometry at all is standing at a depth nobody measured.
 *
 * The backend's old `fallback` flag could not answer this — it was true for every
 * non-survey tier, including the two that carry true depths — which is why the kind
 * exists, and why the flag is gone from this response.
 *
 * @param {ProfileCrossing} crossing - The crossing.
 * @return {boolean} True where real geometry placed the well.
 */
export const positionedByGeometry = (crossing: ProfileCrossing): boolean =>
  typeof crossing.geometrySourceKind === 'string';

/**
 * Whether nothing measured this crossing's depth.
 *
 * The backend says so by leaving the TVD null, having stood the well on the section's
 * ground line instead. It still sends the old nominal thousand feet where it has no
 * DEM under the well and cannot re-reference it, so both readings answer the same
 * question and both are taken.
 *
 * Either way the well must have been positioned by nothing: one with no geometry but a
 * reported total depth has a coarse measurement rather than a made-up one, and belongs
 * at the depth it reports.
 *
 * @param {ProfileCrossing} crossing - The crossing.
 * @return {boolean} True where nothing measured the depth.
 */
export const hasNoMeasuredDepth = (crossing: ProfileCrossing): boolean =>
  !positionedByGeometry(crossing) &&
  (crossing.tvdFt === null ||
    crossing.tvdFt === undefined ||
    crossing.tvdFt === NOMINAL_FALLBACK_TVD_FT);

/**
 * The crossings, with any standing at a nominal depth stood at the surface instead.
 *
 * A well with no survey and no reported TVD comes back at a nominal thousand feet, on
 * a section that runs to eight thousand — a number nothing measured, which used to be
 * drawn just above the shallowest measured depth. That put the well somewhere it had
 * never been, and moved it whenever the terrain was switched on or off, since what was
 * shallowest changed with it.
 *
 * So it stands at the surface: the terrain where that is drawn, and zero — ground level
 * in TVD, sea level in TVDSS — where it is not. The well is on the section and can be
 * picked, at the one depth on the plot that claims nothing about where it ends.
 *
 * A flattened section has no surface — depths are read against a datum — so there the
 * old lift above the shallowest measured depth still applies, for want of anything
 * truer to stand it at.
 *
 * A survey-less well that DOES have a reported total depth is left where it is: that
 * depth is a measurement, coarse though it is, and the axis is fitted to include it.
 *
 * @param {Array} crossings - The crossings, as the cut placed them.
 * @param {ProfilePlanInput} input - The plan input, for the surface.
 * @param {Array | null} extent - The depth extent of everything measured.
 * @return {Array} The crossings, with the nominal ones stood at the surface.
 */
const placePlaceholders = (
  crossings: ProfileCrossing[],
  input: ProfilePlanInput,
  extent: [number, number] | null
): ProfileCrossing[] => {
  if (!crossings.some(hasNoMeasuredDepth)) {
    return crossings;
  }
  const lifted = liftedAboveMeasured(extent, Boolean(input.invertDepthAxis));
  return crossings.map((crossing) => {
    if (!hasNoMeasuredDepth(crossing)) {
      return crossing;
    }
    const depthFt = surfaceDepthAt(input, crossing.distanceFt) ?? lifted;
    return depthFt === null ? crossing : { ...crossing, depthFt };
  });
};

/**
 * Just above the shallowest thing measured, for a flattened section's placeholders.
 *
 * Inside the margin the axis already grows past its data, so the marker lands on the
 * plot rather than off the top of it.
 *
 * @param {Array | null} extent - The depth extent of everything measured.
 * @param {boolean} inverted - True where the axis counts downward, as TVD does.
 * @return {number | null} The depth, or null with nothing measured to sit above.
 */
const liftedAboveMeasured = (
  extent: [number, number] | null,
  inverted: boolean
): number | null => {
  if (!extent) {
    return null;
  }
  const [low, high] = extent;
  const lift = Math.max(high - low, 100) * PLACEHOLDER_LIFT;
  return inverted ? low - lift : high + lift;
};

/**
 * A depth below everything the plot holds, for an annotation that has to reach the
 * foot of it.
 *
 * The view is not the answer: a zoom moves it, and annotations are staged once per
 * change in what is drawn rather than per frame. So the foot is taken from the data's
 * own extent and pushed well past it, which stays below the plot at any zoom anyone
 * would use.
 *
 * @param {ProfilePlan} plan - The drawing plan.
 * @param {boolean} inverted - True where the axis counts downward, as TVD does.
 * @return {number | null} The depth, or null with nothing drawn to measure from.
 */
export const deepEdge = (
  plan: ProfilePlan,
  inverted: boolean
): number | null => {
  const extent = depthExtentOf(plan);
  if (!extent) {
    return null;
  }
  const [low, high] = extent;
  const span = Math.max(high - low, 100);
  return inverted ? high + span * 10 : low - span * 10;
};

/**
 * Spreads a column of label positions so no two sit closer than `minGap`, moving them
 * as little as possible and keeping them inside `bounds`.
 *
 * A stack of nine horizons within a couple of thousand feet puts every interval name
 * at the same handful of pixels, and they print over each other. Nudging them apart
 * keeps all nine readable — an interval name a few pixels off its line still reads as
 * that line's, where a name buried under two others reads as nothing.
 *
 * Positions come in ascending order and come back in the same order, so the caller can
 * pair them with what they belong to. Works in whatever unit it is given; the chart
 * hands it pixels, since that is what legibility is measured in.
 *
 * @param {Array} values - The wanted positions, ascending.
 * @param {number} minGap - The smallest acceptable distance between two of them.
 * @param {Array} bounds - The [low, high] the column has to stay inside.
 * @return {Array} The adjusted positions, in the order given.
 */
export const spreadLabels = (
  values: number[],
  minGap: number,
  bounds?: [number, number]
): number[] => {
  if (values.length < 2) {
    return [...values];
  }
  const out = [...values];

  // Down from the top: the first one keeps its place, and each after it gives way.
  for (let index = 1; index < out.length; index += 1) {
    if (out[index] - out[index - 1] < minGap) {
      out[index] = out[index - 1] + minGap;
    }
  }

  const high = bounds?.[1];
  if (high !== undefined && out[out.length - 1] > high) {
    // The column grew past the bottom, so slide the whole run back up and let the
    // pass below re-open any gap that closed.
    const shift = out[out.length - 1] - high;
    for (let index = 0; index < out.length; index += 1) {
      out[index] -= shift;
    }
  }

  // Up from the bottom, which is what makes room after a slide.
  for (let index = out.length - 2; index >= 0; index -= 1) {
    if (out[index + 1] - out[index] < minGap) {
      out[index] = out[index + 1] - minGap;
    }
  }

  const low = bounds?.[0];
  if (low !== undefined) {
    for (let index = 0; index < out.length; index += 1) {
      out[index] = Math.max(out[index], low + index * minGap);
    }
  }

  return out;
};
